<template>
	<v-navigation-drawer
		v-model="visible"
		location="right"
		temporary
		width="450"
		class="transactions-drawer"
		:class="colourMode"
	>
		<div class="drawer-header">
			<div class="drawer-title-section">
				<h3>{{ title }}</h3>
				<span v-if="!plan && transactions.length" class="txn-count">{{ transactions.length }} transactions</span>
			</div>
			<button class="close-btn" @click="$emit('close')">
				<v-icon size="20">mdi-close</v-icon>
			</button>
		</div>
		<div class="drawer-content">
			<!-- Plan Summary -->
			<div v-if="plan" class="plan-summary">
				<div class="total-spend-section">
					<div class="total-spend-label">Total Spend</div>
					<div class="total-spend-amount">{{ formatter.format(plan.spent) }}</div>
				</div>
				<div class="plan-card">
					<div class="plan-card-header">
						<span class="plan-card-title">Spending Plan</span>
					</div>
					<div class="plan-card-body">
						<div class="plan-icon">
							<v-icon size="24" color="white">mdi-glass-cocktail</v-icon>
						</div>
						<div class="plan-details">
							<span class="plan-budget">Plan: {{ formatter.format(plan.budget) }}</span>
							<span class="plan-separator">|</span>
							<span :class="plan.remaining >= 0 ? 'plan-remaining' : 'plan-overspent'">
								{{ plan.remaining >= 0 ? formatter.format(plan.remaining) + ' left' : formatter.format(Math.abs(plan.remaining)) + ' over' }}
							</span>
						</div>
					</div>
					<div class="plan-progress">
						<div class="plan-progress-bar">
							<div
								class="plan-progress-fill"
								:style="{ width: plan.percent + '%', background: plan.percent >= 100 ? '#c62828' : '#9EA2D0' }"
							></div>
						</div>
					</div>
				</div>
			</div>

			<!-- Transactions -->
			<div v-if="transactions.length === 0" class="no-transactions">
				<v-icon size="48" color="#ccc">mdi-receipt-text-outline</v-icon>
				<p>No transactions found</p>
			</div>
			<div v-else class="transaction-list">
				<div
					v-for="(txn, index) in transactions"
					:key="index"
					class="transaction-item"
				>
					<div class="txn-icon" :style="{ background: getCategoryColour(txn) }">
						<v-icon size="18" color="white">{{ getCategoryIcon(txn) }}</v-icon>
					</div>
					<div class="txn-details">
						<div class="txn-merchant">{{ txn.merchantName || txn.merchant?.name || 'Unknown' }}</div>
						<div class="txn-meta">
							<span class="txn-date">{{ formatDate(txn.timestamp || txn.transactionDate) }}</span>
							<span class="txn-category">{{ getCategoryName(txn) }}</span>
							<button class="edit-btn" @click="$emit('transaction-click', txn)" title="Change category">
								<v-icon size="14">mdi-pencil</v-icon>
							</button>
						</div>
					</div>
					<div class="txn-amount" :class="txn.amount >= 0 ? 'positive' : 'negative'">
						{{ formatter.format(txn.amount) }}
					</div>
				</div>
			</div>
		</div>
	</v-navigation-drawer>
</template>

<script>
export default {
	name: 'TransactionsDrawer',
	props: {
		modelValue: { type: Boolean, default: false },
		title: { type: String, default: 'Transactions' },
		transactions: { type: Array, default: () => [] },
		plan: { type: Object, default: null },
		formatter: { type: Object, required: true },
		colourMode: { type: String, default: 'light' }
	},
	emits: ['update:modelValue', 'close', 'transaction-click'],
	computed: {
		visible: {
			get() { return this.modelValue; },
			set(val) { this.$emit('update:modelValue', val); }
		}
	},
	methods: {
		formatDate(dateStr) {
			if (!dateStr) return '';
			const date = new Date(dateStr);
			const dateFormatted = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
			const timeFormatted = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
			return `${dateFormatted}, ${timeFormatted}`;
		},
		getCategoryColour(txn) {
			const cls = txn.customerClassification || txn.effectiveClassification;
			const grandparent = cls?.grandParentClassification;
			const colours = {
				0: '#B4D8D7', // Income
				1: '#D5C9C8', // Future
				2: '#9EA2D0', // Everyday
				3: '#413655'  // Committed
			};
			return colours[grandparent] || '#888';
		},
		getCategoryIcon(txn) {
			const cls = txn.customerClassification || txn.effectiveClassification;
			const grandparent = cls?.grandParentClassification;
			const icons = {
				0: 'mdi-cash-plus',
				1: 'mdi-piggy-bank-outline',
				2: 'mdi-cart-outline',
				3: 'mdi-home-outline'
			};
			return icons[grandparent] || 'mdi-credit-card-outline';
		},
		getCategoryName(txn) {
			const cls = txn.customerClassification || txn.effectiveClassification;
			return cls?.mgSubClassification || cls?.mgClassification || 'Uncategorized';
		}
	}
};
</script>

<style scoped>
.transactions-drawer {
	background: var(--light-bg-primary) !important;
}

.transactions-drawer.dark {
	background: var(--dark-bg-secondary) !important;
}

.drawer-header {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	padding: 20px;
	border-bottom: 1px solid var(--light-border);
}

.dark .drawer-header {
	border-color: var(--dark-border);
}

.drawer-title-section h3 {
	margin: 0;
	font-size: 18px;
	color: var(--light-text-primary);
}

.dark .drawer-title-section h3 {
	color: var(--dark-text-primary);
}

.txn-count {
	font-size: 12px;
	color: var(--light-text-secondary);
}

.dark .txn-count {
	color: var(--dark-text-secondary);
}

.close-btn {
	background: transparent;
	border: none;
	padding: 4px;
	border-radius: 4px;
	cursor: pointer;
	color: var(--light-text-secondary);
}

.close-btn:hover {
	background: var(--light-bg-hover);
}

.dark .close-btn {
	color: var(--dark-text-secondary);
}

.dark .close-btn:hover {
	background: var(--dark-bg-hover);
}

.drawer-content {
	padding: 16px;
	overflow-y: auto;
	height: calc(100% - 80px);
}

/* Plan Summary Styles */
.plan-summary {
	margin-bottom: 20px;
}

.total-spend-section {
	text-align: center;
	padding: 16px 0;
}

.total-spend-label {
	font-size: 14px;
	color: var(--light-text-secondary);
	margin-bottom: 4px;
}

.dark .total-spend-label {
	color: var(--dark-text-secondary);
}

.total-spend-amount {
	font-size: 32px;
	color: var(--light-text-primary);
}

.dark .total-spend-amount {
	color: var(--dark-text-primary);
}

.plan-card {
	background: #f8f5f3;
	border-radius: 12px;
	padding: 16px;
}

.dark .plan-card {
	background: var(--dark-bg-primary);
}

.plan-card-header {
	margin-bottom: 12px;
}

.plan-card-title {
	font-size: 16px;
	color: var(--light-text-primary);
}

.dark .plan-card-title {
	color: var(--dark-text-primary);
}

.plan-card-body {
	display: flex;
	align-items: center;
	gap: 12px;
	margin-bottom: 12px;
}

.plan-icon {
	width: 44px;
	height: 44px;
	border-radius: 50%;
	background: #9EA2D0;
	display: flex;
	align-items: center;
	justify-content: center;
}

.plan-details {
	font-size: 14px;
	color: var(--light-text-primary);
}

.dark .plan-details {
	color: var(--dark-text-primary);
}

.plan-separator {
	margin: 0 8px;
	color: var(--light-text-secondary);
}

.plan-remaining {
	color: var(--positive-amount, #2e7d32);
}

.plan-overspent {
	color: var(--delete-color, #c62828);
}

.plan-progress {
	margin-top: 8px;
}

.plan-progress-bar {
	height: 8px;
	background: #e0e0e0;
	border-radius: 4px;
	overflow: hidden;
}

.dark .plan-progress-bar {
	background: #333;
}

.plan-progress-fill {
	height: 100%;
	border-radius: 4px;
	transition: width 0.3s ease;
}

/* Transactions */
.no-transactions {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	height: 200px;
	color: var(--light-text-secondary);
	gap: 12px;
}

.no-transactions p {
	margin: 0;
}

.dark .no-transactions {
	color: var(--dark-text-secondary);
}

.transaction-list {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.transaction-item {
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 12px;
	border-radius: 8px;
	background: var(--light-bg-secondary);
}

.dark .transaction-item {
	background: var(--dark-bg-primary);
}

.txn-icon {
	width: 36px;
	height: 36px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
}

.txn-details {
	flex: 1;
	overflow: hidden;
}

.txn-merchant {
	font-size: 14px;
	color: var(--light-text-primary);
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.dark .txn-merchant {
	color: var(--dark-text-primary);
}

.txn-meta {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-top: 2px;
}

.txn-date {
	font-size: 12px;
	color: var(--light-text-secondary);
}

.dark .txn-date {
	color: var(--dark-text-secondary);
}

.txn-category {
	font-size: 12px;
	color: var(--light-text-secondary);
}

.txn-category::before {
	content: '·';
	margin-right: 8px;
}

.dark .txn-category {
	color: var(--dark-text-secondary);
}

.edit-btn {
	opacity: 0;
	background: none;
	border: none;
	padding: 2px 4px;
	cursor: pointer;
	color: var(--light-text-secondary);
	border-radius: 4px;
	transition: opacity 0.15s, background 0.15s;
	margin-left: auto;
}

.transaction-item:hover .edit-btn {
	opacity: 1;
}

.edit-btn:hover {
	background: rgba(65, 54, 85, 0.1);
	color: var(--brand-purple);
}

.dark .edit-btn {
	color: var(--dark-text-secondary);
}

.dark .edit-btn:hover {
	background: rgba(255, 255, 255, 0.1);
	color: var(--dark-text-primary);
}

.txn-amount {
	font-size: 14px;
	white-space: nowrap;
}

.txn-amount.positive {
	color: var(--positive-amount, #2e7d32);
}

.txn-amount.negative {
	color: var(--light-text-primary);
}

.dark .txn-amount.negative {
	color: var(--dark-text-primary);
}
</style>
